import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../../services/api';

const LANGUAGES = [
  { code: 'auto', label: 'Auto' },
  { code: 'en', label: 'EN' },
  { code: 'sv', label: 'SV' },
  { code: 'de', label: 'DE' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' },
  { code: 'it', label: 'IT' },
  { code: 'nl', label: 'NL' },
  { code: 'fi', label: 'FI' },
  { code: 'no', label: 'NO' },
  { code: 'da', label: 'DA' },
  { code: 'pt', label: 'PT' },
  { code: 'ja', label: 'JA' },
  { code: 'zh', label: 'ZH' },
];

export function useVoiceFood({ addEntry }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [language, setLanguage] = useState('auto');
  const [transcript, setTranscript] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const languageRef = useRef('auto');

  // Keep ref in sync with state so onstop callback always has latest value
  languageRef.current = language;

  const processAudio = useCallback(async (blob) => {
    setIsProcessing(true);
    setTranscript('');
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'voice.webm');

      const token = localStorage.getItem('gym_notebook_auth_token');

      // Upload audio - use relative URL
      const uploadResp = await fetch(`/api/nutrition/voice-food?language=${languageRef.current}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!uploadResp.ok) {
        const errText = await uploadResp.text();
        throw new Error(`Upload failed (${uploadResp.status}): ${errText}`);
      }
      const { task_id } = await uploadResp.json();

      // Poll for result
      let attempts = 0;
      const maxAttempts = 30;
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const statusResp = await fetch(`/api/nutrition/voice-food-status/${task_id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (!statusResp.ok) {
          attempts++;
          continue;
        }
        
        const status = await statusResp.json();

        if (status.status === 'done') {
          if (status.error) {
            toast.error(status.error);
            break;
          }
          if (status.transcript) setTranscript(status.transcript);
          if (status.result?.items?.length > 0) {
            const mealtime = status.result.mealtime || 'lunch';
            let addedCount = 0;
            for (const item of status.result.items) {
              const success = addEntry({
                name: item.name,
                calories: item.calories || 0,
                protein: item.protein || 0,
                carbs: item.carbs || 0,
                fat: item.fat || 0,
                amount: item.amount || 100,
                unit: item.unit || 'g',
                mealTime: mealtime,
                source: 'voice',
              });
              if (success) addedCount++;
            }
            if (addedCount > 0) {
              toast.success(`Added ${addedCount} item${addedCount > 1 ? 's' : ''} via voice`);
            }
          } else {
            toast.info('No food items detected. Try speaking more clearly.');
          }
          break;
        }
        attempts++;
      }

      if (attempts >= maxAttempts) {
        toast.error('Voice processing timed out. Please try again.');
      }
    } catch (err) {
      console.error('Voice food error:', err);
      toast.error('Voice food logging failed: ' + (err.message || 'Unknown error'));
    } finally {
      setIsProcessing(false);
    }
  }, [addEntry]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to use a supported MIME type
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/mp4';
        if (!MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = ''; // Let browser choose
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blobType = mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        await processAudio(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info('Recording... Tap stop when done.');
    } catch (err) {
      console.error('Microphone error:', err);
      toast.error('Could not access microphone. Please allow mic access.');
    }
  }, [processAudio]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  return {
    isRecording,
    isProcessing,
    language,
    setLanguage,
    transcript,
    startRecording,
    stopRecording,
    languages: LANGUAGES,
  };
}
